import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  DanteDirection,
  DanteNetworkState,
  DanteSource,
} from "@production-hub/shared";

const stateKey = (bridgeUrl: string | null | undefined) => [
  "bridge-dante-state",
  bridgeUrl,
];

async function danteRequest(
  bridgeUrl: string,
  path: string,
  init: { method: string; body?: unknown },
): Promise<unknown> {
  const response = await fetch(`${bridgeUrl}/api/dante${path}`, {
    method: init.method,
    headers: { "Content-Type": "application/json" },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      (data as { error?: string } | null)?.error ??
        `Request failed: ${response.status}`,
    );
  }
  return data;
}

/**
 * Live Dante state from the bridge: the stored config (which mirrors the
 * network) plus device and route status. Polled, so changes made in Dante
 * Controller show up without a reload.
 */
export const useBridgeDanteState = (bridgeUrl: string | null | undefined) =>
  useQuery({
    queryKey: stateKey(bridgeUrl),
    queryFn: async () =>
      (await danteRequest(bridgeUrl!, "/state", {
        method: "GET",
      })) as DanteNetworkState,
    enabled: !!bridgeUrl,
    refetchInterval: 2500,
  });

/**
 * Mutations against the bridge's Dante API. Each returns the new state,
 * which replaces the cached one immediately.
 */
export const useBridgeDanteMutations = (
  bridgeUrl: string | null | undefined,
) => {
  const queryClient = useQueryClient();
  const onSuccess = (state: unknown) => {
    if (state && typeof state === "object" && "config" in state) {
      queryClient.setQueryData(stateKey(bridgeUrl), state);
    } else {
      void queryClient.invalidateQueries({ queryKey: stateKey(bridgeUrl) });
    }
  };

  const setRoute = useMutation({
    mutationFn: (args: {
      rxDevice: string;
      rxIndex: number;
      source: DanteSource | null;
    }) =>
      danteRequest(
        bridgeUrl!,
        `/devices/${encodeURIComponent(args.rxDevice)}/rx/${args.rxIndex}/source`,
        { method: "PUT", body: { source: args.source } },
      ),
    onSuccess,
    onError: (err: Error) => toast.error("Dante route failed: " + err.message),
  });

  const rename = useMutation({
    mutationFn: (args: {
      device: string;
      direction: DanteDirection;
      index: number;
      name: string | null;
    }) =>
      danteRequest(
        bridgeUrl!,
        `/devices/${encodeURIComponent(args.device)}/${args.direction}/${args.index}/name`,
        { method: "PUT", body: { name: args.name } },
      ),
    onSuccess,
    onError: (err: Error) => toast.error("Dante rename failed: " + err.message),
  });

  const removeDevice = useMutation({
    mutationFn: (device: string) =>
      danteRequest(bridgeUrl!, `/devices/${encodeURIComponent(device)}`, {
        method: "DELETE",
      }),
    onSuccess,
    onError: (err: Error) =>
      toast.error("Failed to remove Dante device: " + err.message),
  });

  const apply = useMutation({
    mutationFn: async () =>
      (await danteRequest(bridgeUrl!, "/apply", { method: "POST" })) as {
        writes: number;
        errors: string[];
      },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: stateKey(bridgeUrl) });
      if (result.errors.length > 0) {
        toast.error(`Dante apply: ${result.errors.length} change(s) failed`);
      } else {
        toast.success(
          result.writes === 0
            ? "Dante network already matches the config"
            : `Applied ${result.writes} Dante change(s)`,
        );
      }
    },
    onError: (err: Error) => toast.error("Dante apply failed: " + err.message),
  });

  return { setRoute, rename, removeDevice, apply };
};
